import type { AutomergeUrl } from "@automerge/automerge-repo";
import { RepoContext } from "@automerge/react";
import type { UIMessage } from "ai";
import type { ShowInput } from "apprentices/tools/show";
import { DEFAULT_DRAFTER_MODEL_ID } from "apprentices/worker/models";
import {
  chatCommands,
  type ChatCommands,
  type ChatFeedView,
  mintId,
  splitWaitingSends,
  useChatFeed,
  useChatSend,
} from "den/client/use-chat-feed";
import { ChatPane } from "forest/chat/ChatPane";
import type { RailCheckpoint } from "forest/chat/checkpoint-graph";
import { usePlaygroundFileContext } from "forest/chat/file-mentions";
import { ToolApprovalCard } from "forest/chat/ToolCallView";
import type {
  AnyToolPart,
  ApprovalCopy,
  RenderToolCard,
  RespondToApproval,
  SentMessage,
} from "forest/chat/types";
import type { CheckpointRailState } from "forest/chat/use-checkpoint-rail";
import { useTreeRevision } from "forest/file-tree/use-tree";
import { useAppHostRegistry } from "forest/sandbox/AppHostRegistry";
import {
  GitBranch,
  MessageSquare,
  MessageSquareText,
  Trash2,
  XIcon,
} from "lucide-react";
import type { CommentsInput } from "playground/annotations/agent";
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { commentAskOf } from "@/comments/ask-bear";
import { useBearSandbox } from "@/components/BearSandboxProvider";
import { DrafterModelPicker } from "@/components/DrafterModelPicker";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { sanitizeErrorMessage } from "@/lib/retry-fetch";
import {
  createChat,
  fetchChat,
  type Chat,
  type ProjectChats,
} from "@/panes/use-project-chats";
import { useSandboxTools } from "@/panes/use-sandbox-tools";
import { useWorkspace } from "@/panes/Workspace";
import { workspaceContextParts } from "@/panes/workspace-context-parts";
import type { Project } from "@/types/projects";

/** The draft thread's view: nothing yet, nothing running. */
const EMPTY_VIEW: ChatFeedView = { messages: [], phase: "idle" };

/**
 * The drafter chat, on the shared chat shell (`forest/chat`) over den's
 * durable thread feed (SUP-273). The turn runs in `DrafterHostDO`; this pane
 * owns what is Bear's — the model pick a NEW thread pins and the
 * sandbox-executed tools (`use-sandbox-tools`). A reload mid-stream rejoins
 * the same turn; there is no client queue, no client compaction, and no SSE —
 * the ledger serializes turns and the host compacts.
 *
 * Which thread is open is NOT this pane's: the file pane lists them, so the
 * selection lives above both (`useProjectChats`).
 */
interface BuilderPaneProps {
  projectId: string;
  taskId?: string;
  showHeader?: boolean;
  /** What stands where the composer would: a closed task's way onward. */
  composerReplacement?: ReactNode;
  /** The project doc, for the `@`-mention file list and context snapshots. */
  project: Project;
  /** The project index doc: the sandbox's checkpoint tool writes through it. */
  indexUrl: AutomergeUrl;
  /**
   * The thread to run. `null` is a DRAFT: no chat row yet, the model picker is
   * live, and the first send creates the row. The route only mounts this pane
   * for an open chat, so "nothing open" is not a state it can be in.
   */
  chatId: string | null;
  /** The project's threads: the list, and where a new row lands. */
  chats: ProjectChats;
  /**
   * The checkpoint log. The rail itself is in the project panel; what this
   * pane needs is which node the workspace is on, to tell the model.
   */
  checkpoints: CheckpointRailState;
  /** The file the member is looking at, auto-attached as context. */
  activeFile?: string | null;
  /** The `show` tool's hands: open the named pane and reveal within it. */
  onShow: (input: ShowInput) => Promise<string>;
  /** Observe the durable feed without giving the agent a context-writing tool. */
  onFeedMessages?: (messages: UIMessage[]) => void;
  /**
   * The conversation now works somewhere this pane is not showing (`null` is
   * main): its own `task` tool moved it, or the member did. The host follows.
   */
  onScopeMove: (chatId: string, taskId: string | null) => void;
}

const BuilderPane: React.FC<BuilderPaneProps> = ({
  projectId,
  taskId,
  showHeader = true,
  composerReplacement,
  project,
  indexUrl,
  chatId,
  chats,
  checkpoints,
  activeFile,
  onShow,
  onFeedMessages,
  onScopeMove,
}) => {
  const [modelId, setModelId] = useState<string>(DEFAULT_DRAFTER_MODEL_ID);
  const { run, playground } = useBearSandbox();
  const repo = useContext(RepoContext);
  const name = chatId ? `${projectId}:${chatId}` : "";
  const feed = useChatFeed("drafter-host", name, {
    enabled: chatId !== null,
  });

  // A draft's first send is the one command that must exist before the
  // thread does: create the row, send against the new name, then adopt it.
  // The ref holds the create PROMISE, not the row — two sends racing before
  // the POST lands must share one creation, or each would mint its own chat
  // and split the messages across threads (Codex, #1474).
  const createdRef = useRef<Promise<Chat> | null>(null);
  // The promise belongs to the draft session that started it. Switching
  // threads (including back to a fresh draft) ends that session, so a later
  // draft send can't adopt the chat an earlier one created.
  useEffect(() => {
    createdRef.current = null;
  }, [chatId]);
  const adoptChat = chats.adopt;
  const commands = useMemo<ChatCommands>(() => {
    const opts = { agentClass: "drafter-host" };
    if (chatId) return chatCommands(`${projectId}:${chatId}`, opts);
    return {
      async sendMessage(requestId, message) {
        createdRef.current ??= createChat(projectId, modelId, taskId);
        let chat: Chat;
        try {
          chat = await createdRef.current;
        } catch (err) {
          // A failed create must not poison every later send.
          createdRef.current = null;
          throw err;
        }
        await chatCommands(`${projectId}:${chat.id}`, opts).sendMessage(
          requestId,
          message,
        );
        adoptChat(chat);
      },
      stop: async () => {},
      approveTool: async () => {},
      submitToolResult: async () => {},
      clear: async () => {},
    };
  }, [chatId, projectId, modelId, taskId, adoptChat]);

  const view = feed.view ?? (chatId === null ? EMPTY_VIEW : null);
  const messages = view?.messages ?? [];
  const phase = view?.phase ?? "idle";
  const { pending, status, error, send, perform } = useChatSend(commands, view);
  // Accepted-but-not-yet-driven sends render as queued bubbles below the
  // running turn's activity, not as transcript.
  const { transcript, waiting } = splitWaitingSends(messages, phase);
  const queued = [...waiting, ...pending];

  const jigHosts = useAppHostRegistry();
  // Sandbox-executed tool calls are feed state; drive them from it.
  const sandboxTools = useSandboxTools(commands.submitToolResult, {
    projectId,
    chatId,
    taskId,
    run,
    files: project.files,
    checkpoints: { repo, indexUrl, url: project.checkpointsDocUrl },
    show: onShow,
    playground,
    jigHosts,
  });
  // A `task` tool result means the row moved: re-read it, and remember
  // which move the row on screen has caught up with.
  const lastMove = messages
    .flatMap((m) => m.parts)
    .findLast(
      (part) => part.type === "tool-task" && part.state === "output-available",
    );
  const lastMoveId =
    lastMove && "toolCallId" in lastMove ? lastMove.toolCallId : undefined;
  // This conversation's own row, read whenever it may have moved: after its
  // `task` tool moves it, and whenever the project's list is re-read (a turn
  // settled, a member moved it). Read on its own, not off the list, which
  // holds only the most recent chats. It records the move it reflects.
  const [row, setRow] = useState<{ chat: Chat; move?: string } | null>(null);
  const refreshList = chats.refresh;
  const listed = chats.list;
  useEffect(() => {
    if (!chatId) return;
    let live = true;
    void fetchChat(chatId).then((chat) => {
      if (live) setRow({ chat, move: lastMoveId });
    });
    return () => {
      live = false;
    };
  }, [chatId, lastMoveId, listed]);
  useEffect(() => {
    if (lastMoveId) void refreshList();
  }, [lastMoveId, refreshList]);
  const currentChat = row?.chat.id === chatId ? row.chat : undefined;

  // Where the conversation works is its row's; a draft works where it is.
  // Sandbox tools run against THIS pane's branch, so they only run once the
  // row is here, reflects the latest move, and names this pane's scope: a
  // call made after a move waits for the pane that follows it.
  const rowMatches = currentChat
    ? currentChat.task_id === (taskId ?? null)
    : chatId === null;
  const inScope = rowMatches && row?.move === lastMoveId;
  useEffect(() => {
    if (view && inScope) sandboxTools.onMessages(view.messages);
  }, [view, inScope, sandboxTools]);

  // Follow a move that happens while this pane shows the conversation. One
  // that happened before (an old task's page) is a notice, not a redirect.
  const rowScope = currentChat?.task_id;
  const shownScope = useRef({ chatId, scope: rowScope });
  useEffect(() => {
    const before = shownScope.current;
    shownScope.current = { chatId, scope: rowScope };
    if (
      chatId &&
      before.chatId === chatId &&
      rowScope !== undefined &&
      before.scope === (taskId ?? null) &&
      rowScope !== before.scope
    ) {
      onScopeMove(chatId, rowScope);
    }
  }, [chatId, rowScope, taskId, onScopeMove]);
  useEffect(() => {
    if (view) onFeedMessages?.(view.messages);
  }, [view, onFeedMessages]);

  // Refresh the list when a turn settles, so titles/previews track the
  // settle envelope. Keyed on the settled artifact, not phase transitions.
  const refreshChats = chats.refresh;
  const lastAssistantId =
    phase === "idle"
      ? messages.findLast((m) => m.role === "assistant")?.id
      : undefined;
  const settled = useRef<{ primed: boolean; id: string | undefined }>({
    primed: false,
    id: undefined,
  });
  useEffect(() => {
    if (phase !== "idle") return;
    const prev = settled.current;
    if (prev.primed && prev.id !== lastAssistantId) void refreshChats();
    settled.current = { primed: true, id: lastAssistantId };
  }, [phase, lastAssistantId, refreshChats]);

  // The pane that owns the socket is the only one that knows a turn is in
  // flight, and the checkpoint rail — now in the file pane — has to lock while
  // one is writing to the project. The cleanup unlocks on unmount, so closing
  // the builder mid-turn doesn't strand the rail.
  const setBusy = chats.setBusy;
  const inFlight = status === "streaming" || status === "submitted";
  useEffect(() => {
    setBusy(inFlight);
    return () => setBusy(false);
  }, [inFlight, setBusy]);

  // Picking a thread in the file panel puts the cursor here. The pane isn't
  // remounted on a switch, so this is an imperative focus rather than the
  // composer's own `autoFocus`, which only fires on mount.
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const focusRequest = chats.focusRequest;
  useEffect(() => {
    if (focusRequest === 0) return;
    composerRef.current?.focus();
  }, [focusRequest]);

  // `@`-mention list + send-time snapshots, through the sandbox's Playground.
  const fileContext = usePlaygroundFileContext(
    playground,
    useTreeRevision(playground),
  );
  const { buildWorkspaceContext, registerContextBuilder } = useWorkspace();

  /**
   * The pane state the member is looking at (focused sketch, screenshots),
   * converted HERE — the tldraw-backed converters live in the browser with
   * the editors that feed them — and sent as a hidden `<workspace_context>`
   * text part ON the message, with a screenshot as a file part beside it
   * (dropping it here is how the model once lost every sketch). The Bun path injected this into the system
   * prompt from a request-body side channel; message-borne context is what
   * the durable model wants (a recovery retry rebuilds the exact prompt) and
   * it rides the same tags the composer's file snapshots already use, so the
   * transcript skips it and the settle preview filters it for free.
   *
   * The clock leads it and is unconditional — a send always states when it was
   * sent, whatever panes are open.
   */
  const workspaceContextPart = async () => {
    const parts = await buildWorkspaceContext().catch((err: unknown) => {
      console.error("Failed to build workspace context", err);
      return [];
    });
    return workspaceContextParts(new Date(), parts);
  };

  // Tell the model which branch it is on, as one of the context parts above.
  // Restoring from the rail is not a tool call, so the transcript never
  // mentions it: read from the live log through a ref rather than
  // re-registering, so what goes out is the state at send time and not
  // whenever this last rendered.
  const activeRef = useRef<RailCheckpoint | undefined>(undefined);
  activeRef.current = checkpoints.graph.rows.find(
    (r) => r.isActive,
  )?.checkpoint;
  useEffect(
    () =>
      registerContextBuilder(() => {
        const active = activeRef.current;
        if (!active) return undefined;
        return {
          type: "data-checkpoints",
          id: "checkpoints",
          data: { id: active.id },
        };
      }),
    [registerContextBuilder],
  );

  const handleSend = (sent: SentMessage) => {
    if (!sent.text && sent.files.length === 0) return;
    void (async () => {
      const message: UIMessage = {
        id: mintId(),
        role: "user",
        parts: [
          ...(sent.contextParts ?? []),
          ...(await workspaceContextPart()),
          ...(sent.text ? [{ type: "text" as const, text: sent.text }] : []),
          ...sent.files,
        ],
      };
      send(message, sent);
    })();
  };

  // Comment threads keep their own words; the chat shows one line for each.
  const renderToolCard = useMemo(() => toolCards(onShow), [onShow]);
  const renderActivity = useCallback(
    (message: UIMessage) => {
      const ask = commentAskOf(message);
      return ask ? (
        <ThreadLine
          label={`Asked Bear in a comment on ${ask.path}`}
          detail={ask.messages.at(-1)?.body}
          path={ask.path}
          threadId={ask.threadId}
          onShow={onShow}
        />
      ) : undefined;
    },
    [onShow],
  );

  const handleStop = () => {
    void perform(() => commands.stop()).catch(() => {});
  };

  // `perform` rethrows, so the approval card re-enables itself on failure.
  const handleToolApproval: RespondToApproval = (toolCallId, approved) =>
    void perform(() => commands.approveTool(toolCallId, approved)).catch(
      () => {},
    );

  // A live thread's model is pinned; only a draft chooses.
  const pinnedModelId = chatId ? currentChat?.model_id : null;
  const shownModelId = pinnedModelId ?? modelId;
  // Not a DRAFTER_MODELS lookup: a live thread can be pinned to a model we
  // have since retired, and it should still show its own name and logo.

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ChatPane
        messages={transcript}
        status={status}
        error={error}
        onSend={handleSend}
        onStop={handleStop}
        queued={queued}
        onToolApproval={handleToolApproval}
        approvalCopy={describeApproval}
        renderToolCard={renderToolCard}
        renderActivity={renderActivity}
        standsAlone={standsAlone}
        // Which thread this is, and the way out of it. Starting another is the
        // header's job and the project panel's; this bar only says where you
        // are, which a transient pane has to.
        header={
          showHeader ? (
            <div className="flex h-10 shrink-0 items-center justify-between gap-2 border-b pr-2 pl-3">
              <span className="min-w-0 truncate text-sm font-medium">
                {chatId === null
                  ? "New chat"
                  : (currentChat?.title ?? "Untitled chat")}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                title="Close chat"
                aria-label="Close chat"
                onClick={chats.close}
              >
                <XIcon />
              </Button>
            </div>
          ) : undefined
        }
        connected={chatId === null || feed.connected}
        composerReplacement={
          !rowMatches && chatId && currentChat ? (
            <div className="flex items-center gap-3 border-t px-4 py-3 text-xs text-muted-foreground">
              <span className="min-w-0 flex-1">
                {currentChat.task_id
                  ? `This conversation continued in “${currentChat.task_title}”.`
                  : "This conversation continued on main."}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onScopeMove(chatId, currentChat.task_id)}
              >
                Open
              </Button>
            </div>
          ) : (
            composerReplacement
          )
        }
        emptyState={
          <li className="flex flex-col items-center gap-2 py-12 text-center">
            <MessageSquare className="size-12 text-muted-foreground/50" />
            <div className="text-sm font-medium">Start a conversation</div>
            <div className="text-sm text-muted-foreground">
              Request changes or ask a question to begin
            </div>
          </li>
        }
        renderError={(err) => (
          <li>
            <Alert variant="destructive">
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>
                {sanitizeErrorMessage(
                  err.message || "An unexpected error occurred.",
                )}
              </AlertDescription>
            </Alert>
          </li>
        )}
        composerRef={composerRef}
        composerPlaceholder="Request changes or ask a question"
        allowAttachments
        fileContext={fileContext}
        openFilePath={activeFile ?? null}
        composerFooterExtras={({ disabled }) => (
          <DrafterModelPicker
            value={shownModelId}
            onChange={chatId === null ? setModelId : undefined}
            disabled={disabled}
          />
        )}
      />
    </div>
  );
};

/** A file removal the agent is asking to run — the one destructive ask. */
function isRemovalAsk(part: AnyToolPart): boolean {
  if (part.state !== "approval-requested") return false;
  const record = (part.input ?? {}) as Record<string, unknown>;
  return typeof record.path === "string";
}

/** A scope move reads as one line: where the conversation works now. */
function ScopeMoveCard({ part }: { part: AnyToolPart }) {
  const output = part.output as { scope?: string } | undefined;
  return (
    <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
      <GitBranch className="size-3.5 shrink-0" />
      <span>
        {part.state === "output-available"
          ? output?.scope
          : part.state === "output-error"
            ? part.errorText
            : "Moving this conversation…"}
      </span>
    </div>
  );
}

/** A comment thread in the chat: one line that opens the thread. */
function ThreadLine({
  label,
  detail,
  path,
  threadId,
  onShow,
}: {
  label: string;
  detail?: string;
  path: string;
  threadId: string;
  onShow: (input: ShowInput) => Promise<string>;
}) {
  return (
    <button
      type="button"
      className="flex w-full min-w-0 items-start gap-2 rounded-md py-1 text-left text-xs text-muted-foreground hover:text-foreground"
      onClick={() => void onShow({ kind: "comment", path, threadId })}
    >
      <MessageSquareText className="mt-0.5 size-3.5 shrink-0" />
      <span className="min-w-0">
        <span className="block">{label}</span>
        {detail ? (
          <span className="block truncate text-foreground">{detail}</span>
        ) : null}
      </span>
    </button>
  );
}

/** What the agent's comments tool did, in one line each. */
const COMMENT_ACTIONS = {
  list: (path: string) => `Read the comments on ${path}`,
  reply: (path: string) => `Replied in a comment on ${path}`,
  resolve: (path: string) => `Resolved a comment on ${path}`,
  reopen: (path: string) => `Reopened a comment on ${path}`,
};

function CommentsToolLine({
  part,
  onShow,
}: {
  part: AnyToolPart;
  onShow: (input: ShowInput) => Promise<string>;
}) {
  const input = part.input as Partial<CommentsInput> | undefined;
  if (!input?.action || !input.path) return null;
  const label = COMMENT_ACTIONS[input.action](input.path);
  if (part.state === "output-error") {
    return (
      <div className="py-1 text-xs text-destructive">{part.errorText}</div>
    );
  }
  return "threadId" in input && input.threadId ? (
    <ThreadLine
      label={label}
      path={input.path}
      threadId={input.threadId}
      onShow={onShow}
    />
  ) : (
    <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
      <MessageSquareText className="size-3.5 shrink-0" />
      {label}
    </div>
  );
}

/** A scope move or a thread link is a landmark: it never folds into a run. */
const standsAlone = (part: AnyToolPart) =>
  part.type === "tool-task" ||
  (part.type === "tool-comments" &&
    (part.input as Partial<CommentsInput> | undefined)?.action !== "list");

/**
 * A removal keeps its destructive framing: red panel, and the affirmative
 * button says what it does rather than "Approve". Same card as every other
 * approval otherwise — a member shouldn't have to learn a second shape to
 * answer a second kind of question.
 */
const toolCards =
  (onShow: (input: ShowInput) => Promise<string>): RenderToolCard =>
  ({ part, modelName }) =>
    part.type === "tool-task" ? (
      <ScopeMoveCard part={part} />
    ) : part.type === "tool-comments" ? (
      <CommentsToolLine part={part} onShow={onShow} />
    ) : isRemovalAsk(part) ? (
      <ToolApprovalCard
        part={part}
        copy={describeApproval(modelName, part.input)}
        alarming
        primaryAction={{
          label: "Remove",
          icon: <Trash2 className="mr-1 h-3.5 w-3.5" />,
          variant: "destructive",
          run: async () => true,
        }}
      />
    ) : undefined;

/**
 * What an approval card says. The only destructive ask deletes a path, so the
 * card names the path and what happens to it — a member deciding "yes" needs to
 * read the thing that will be gone.
 */
function describeApproval(modelName: string, input: unknown): ApprovalCopy {
  const record = (input ?? {}) as Record<string, unknown>;
  const path = typeof record.path === "string" ? record.path : null;
  if (!path) return { title: `Allow ${modelName}?` };
  return {
    title:
      record.recursive === true
        ? `Remove ${path} and everything inside it?`
        : `Remove ${path}?`,
    icon: <Trash2 className="h-4 w-4 text-destructive" />,
  };
}

export default BuilderPane;
